/* eslint-disable @typescript-eslint/no-explicit-any */
'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { preconnect } from 'react-dom';
import { Form, Alert } from 'antd';
import { useRouter } from 'next/navigation';
import Image from 'next/image';
import Link from 'next/link';
import { FiArrowLeft } from 'react-icons/fi';

import { useAppDispatch } from '@/hooks/useRedux';
import {
  postSignInIntroduction,
  postSignInIdentification,
  postSignInConfirmation,
} from '@/redux/auth/signin/thunk';

import Introduction from './signinSteps/Introduction';
import Identification from './signinSteps/Identification';
import OTP from './signinSteps/OTP';
import Logo from '@/components/layout/Logo';
import ThemedButton from '@/components/common/buttons/Button';
import AuthFooter from '../AuthFooter';
import { getProfessionalErrorMessage } from '@/utils/errorHandling';
import { getBaseApi } from '@/utils/runtimeOrg';
import {
  clearRememberedId,
  purgeLegacySigninStorage,
  readRememberedId,
  saveRememberedId,
} from '@/utils/rememberedLogin';

type IdPayload = { id_type: string; value: string; password: string };

const DEFAULT_ID_TYPE = 'fc09df21-5606-4c8a-a921-199bd7b0ce64';

const GridBackground = () => (
  <svg
    className="pointer-events-none absolute inset-0 h-full w-full"
    width="100%"
    height="100%"
    viewBox="0 0 750 560"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <mask
      id="mask0_35_26103"
      style={{ maskType: 'alpha' }}
      maskUnits="userSpaceOnUse"
      x={-9}
      y={-208}
      width={768}
      height={768}
    >
      <rect
        width="768"
        height="768"
        transform="translate(-9 -208)"
        fill="url(#paint0_radial_35_26103)"
      />
    </mask>
    <g mask="url(#mask0_35_26103)">
      <g clipPath="url(#clip0_35_26103)">
        <g clipPath="url(#clip1_35_26103)">
          <line x1="39.5" y1="-208" x2="39.5" y2="560" stroke="#E9EAEB" />
          <line x1="87.5" y1="-208" x2="87.5" y2="560" stroke="#E9EAEB" />
          <line x1="135.5" y1="-208" x2="135.5" y2="560" stroke="#E9EAEB" />
          <line x1="183.5" y1="-208" x2="183.5" y2="560" stroke="#E9EAEB" />
          <line x1="231.5" y1="-208" x2="231.5" y2="560" stroke="#E9EAEB" />
          <line x1="279.5" y1="-208" x2="279.5" y2="560" stroke="#E9EAEB" />
          <line x1="327.5" y1="-208" x2="327.5" y2="560" stroke="#E9EAEB" />
          <line x1="375.5" y1="-208" x2="375.5" y2="560" stroke="#E9EAEB" />
          <line x1="423.5" y1="-208" x2="423.5" y2="560" stroke="#E9EAEB" />
          <line x1="471.5" y1="-208" x2="471.5" y2="560" stroke="#E9EAEB" />
          <line x1="519.5" y1="-208" x2="519.5" y2="560" stroke="#E9EAEB" />
          <line x1="567.5" y1="-208" x2="567.5" y2="560" stroke="#E9EAEB" />
          <line x1="615.5" y1="-208" x2="615.5" y2="560" stroke="#E9EAEB" />
          <line x1="663.5" y1="-208" x2="663.5" y2="560" stroke="#E9EAEB" />
          <line x1="711.5" y1="-208" x2="711.5" y2="560" stroke="#E9EAEB" />
        </g>
        <rect x="-8.5" y="-207.5" width="767" height="767" stroke="#E9EAEB" />
        <g clipPath="url(#clip2_35_26103)">
          <line x1="-9" y1="31.5" x2="759" y2="31.5" stroke="#E9EAEB" />
          <line x1="-9" y1="79.5" x2="759" y2="79.5" stroke="#E9EAEB" />
          <line x1="-9" y1="127.5" x2="759" y2="127.5" stroke="#E9EAEB" />
          <line x1="-9" y1="175.5" x2="759" y2="175.5" stroke="#E9EAEB" />
          <line x1="-9" y1="223.5" x2="759" y2="223.5" stroke="#E9EAEB" />
          <line x1="-9" y1="271.5" x2="759" y2="271.5" stroke="#E9EAEB" />
          <line x1="-9" y1="319.5" x2="759" y2="319.5" stroke="#E9EAEB" />
          <line x1="-9" y1="367.5" x2="759" y2="367.5" stroke="#E9EAEB" />
          <line x1="-9" y1="415.5" x2="759" y2="415.5" stroke="#E9EAEB" />
          <line x1="-9" y1="463.5" x2="759" y2="463.5" stroke="#E9EAEB" />
          <line x1="-9" y1="511.5" x2="759" y2="511.5" stroke="#E9EAEB" />
          <line x1="-9" y1="559.5" x2="759" y2="559.5" stroke="#E9EAEB" />
        </g>
        <rect x="-8.5" y="-207.5" width="767" height="767" stroke="#E9EAEB" />
      </g>
    </g>
    <defs>
      <radialGradient
        id="paint0_radial_35_26103"
        cx="0"
        cy="0"
        r="1"
        gradientUnits="userSpaceOnUse"
        gradientTransform="translate(384 384) rotate(90) scale(384 384)"
      >
        <stop />
        <stop offset="1" stopOpacity="0" />
      </radialGradient>
      <clipPath id="clip0_35_26103">
        <rect
          width="768"
          height="768"
          fill="white"
          transform="translate(-9 -208)"
        />
      </clipPath>
      <clipPath id="clip1_35_26103">
        <rect x="-9" y="-208" width="768" height="768" fill="white" />
      </clipPath>
      <clipPath id="clip2_35_26103">
        <rect x="-9" y="-208" width="768" height="768" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

const SigninPage = () => {
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const router = useRouter();

  const [activeStep, setActiveStep] = useState<number>(0);
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [redirecting, setRedirecting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const stepRef = useRef(0);
  const sessionIdRef = useRef<string | null>(null);
  const credentialsRef = useRef<IdPayload | null>(null);
  const submittingRef = useRef(false);
  const redirectingRef = useRef(false);

  const goToStep = (step: number) => {
    stepRef.current = step;
    setActiveStep(step);
  };

  const isSafeRel = (p?: string | null) => !!p && /^\/(?!\/)/.test(p);

  const getNextFromURL = (): string | null => {
    if (typeof window === 'undefined') return null;
    const next = new URLSearchParams(window.location.search).get('next');
    return isSafeRel(next) ? next! : null;
  };

  const getNextFromSession = (): string | null => {
    try {
      if (typeof window === 'undefined') return null;
      const next = sessionStorage.getItem('postLoginRedirect');
      return isSafeRel(next) ? next! : null;
    } catch {
      return null;
    }
  };

  const pickNextDestination = (): string =>
    getNextFromURL() || getNextFromSession() || '/account';

  const clearStashedNext = () => {
    try {
      sessionStorage.removeItem('postLoginRedirect');
      sessionStorage.removeItem('postLoginRedirectAbs');
    } catch {}
  };

  useEffect(() => {
    purgeLegacySigninStorage();

    const remembered = readRememberedId();
    if (remembered) form.setFieldsValue({ value: remembered, remember: true });

    try {
      preconnect(new URL(getBaseApi()).origin, { crossOrigin: 'anonymous' });
    } catch {}

    (router as any)?.prefetch?.(pickNextDestination());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stepMeta = useMemo(
    () => [
      {
        title: 'Step 1: Introduction',
        description:
          'Tell us your nationality and whether you are an alien or a refugee.',
      },
      {
        title: 'Step 2: Identification',
        description:
          'Enter your identification document type, the document value, and your account password.',
      },
      {
        title: 'Step 3: OTP Confirmation',
        description:
          'Enter the one-time code sent to your registered contact to complete sign in.',
      },
    ],
    [],
  );

  const currentMeta = stepMeta[activeStep] ?? stepMeta[0];

  const handleIntroNext = async (values: any) => {
    setInlineError(null);

    const res = await dispatch(
      postSignInIntroduction({
        nationality: values.nationality ?? 'KE',
        is_alien: !!values.is_alien,
        is_refugee: !!values.is_refugee,
      }),
    ).unwrap();

    sessionIdRef.current = res?.session_id ?? null;
    goToStep(1);
  };

  const handleIdentificationNext = async (values: any) => {
    setInlineError(null);

    const sessionId = sessionIdRef.current;
    if (!sessionId) {
      setInlineError('Session expired or missing. Please restart sign in.');
      return;
    }

    const creds: IdPayload = {
      id_type: values.id_type || DEFAULT_ID_TYPE,
      value: String(values.value ?? '').trim(),
      password: values.password,
    };

    await dispatch(
      postSignInIdentification({ session_id: sessionId, ...creds }),
    ).unwrap();

    credentialsRef.current = creds;
    goToStep(2);
  };

  const handleOTPSubmit = async (values: any) => {
    setInlineError(null);

    const otp_code =
      typeof values.otp_code === 'string'
        ? values.otp_code.trim()
        : String(values.otp_code ?? '');

    if (!otp_code) {
      setInlineError('Please enter the OTP code.');
      return;
    }

    const sessionId = sessionIdRef.current;
    if (!sessionId) {
      setInlineError('Session expired or missing. Please restart sign in.');
      return;
    }

    const creds = credentialsRef.current;
    if (!creds) {
      setInlineError('Missing identification details. Please redo Step 2.');
      goToStep(1);
      return;
    }

    await dispatch(
      postSignInConfirmation({ session_id: sessionId, ...creds, otp_code }),
    ).unwrap();

    if (form.getFieldValue('remember')) saveRememberedId(creds.value);
    else clearRememberedId();

    sessionIdRef.current = null;
    credentialsRef.current = null;

    redirectingRef.current = true;
    setRedirecting(true);

    const dest = pickNextDestination();
    clearStashedNext();
    router.push(dest);
  };

  const handleResendOtp = async () => {
    setInlineError(null);

    const sessionId = sessionIdRef.current;
    const creds = credentialsRef.current;

    if (!sessionId) {
      goToStep(0);
      return;
    }
    if (!creds) {
      goToStep(1);
      return;
    }

    try {
      await dispatch(
        postSignInIdentification({ session_id: sessionId, ...creds }),
      ).unwrap();
    } catch (err: unknown) {
      const msg =
        err instanceof Error
          ? err.message
          : 'Failed to resend code. Please go back and try again.';
      setInlineError(getProfessionalErrorMessage(msg));
    }
  };

  const handleNext = async (values: any = {}) => {
    if (submittingRef.current || redirectingRef.current) return;

    submittingRef.current = true;
    setIsSubmitting(true);
    const stepAtStart = stepRef.current;
    try {
      if (stepAtStart === 0) await handleIntroNext(values);
      else if (stepAtStart === 1) await handleIdentificationNext(values);
      else await handleOTPSubmit(values);
    } catch (err: any) {
      if (!err?.errorFields && !Array.isArray(err)) {
        const raw = typeof err === 'string' ? err : err?.message;
        setInlineError(
          getProfessionalErrorMessage(
            raw || 'An unexpected error occurred. Please try again.',
          ),
        );
      }
    } finally {
      if (!redirectingRef.current && stepRef.current === stepAtStart) {
        submittingRef.current = false;
      }
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (!redirectingRef.current) submittingRef.current = false;
  }, [activeStep]);

  const handlePrev = () => {
    if (stepRef.current > 0) {
      setInlineError(null);
      goToStep(stepRef.current - 1);
    }
  };

  const handleCancel = () => {
    const remembered = readRememberedId();
    sessionIdRef.current = null;
    credentialsRef.current = null;
    form.resetFields();
    if (remembered) form.setFieldsValue({ value: remembered, remember: true });
    setInlineError(null);
    goToStep(0);
  };

  return (
    <div className="relative w-full flex min-h-screen items-stretch justify-center bg-white px-4 md:px-8">
      <div
        className={`flex w-full max-w-[1200px] flex-col bg-white md:flex-row ${
          redirecting ? 'pointer-events-none opacity-60' : 'opacity-100'
        } transition-opacity`}
      >
        <div className="relative flex flex-1 flex-col items-center overflow-hidden bg-white px-6 py-8 md:px-12 md:py-12 shadow">
          <GridBackground />

          <div className="relative z-10 flex w-full max-w-[480px] flex-1 flex-col justify-center gap-6">
            <div className="flex justify-center items-center gap-0.5">
              <Logo
                href="/"
                className="w-[180px]"
                imgClassName="max-h-[56px]"
              />
            </div>

            <div className="text-left">
              <div className="mb-12 text-center">
                <h1 className="text-2xl sm:text-3xl font-medium mb-2 text-gray-900">
                  Welcome Back
                </h1>
                <p className="max-w-full text-center mx-auto text-lg leading-relaxed text-gray-600">
                  Log in to manage your county services, make payments, and
                  track your transactions easily.
                </p>
              </div>

              <p className="mt-3 text-xs font-semibold uppercase tracking-[0.2em] text-gray-400">
                Step {activeStep + 1} of 3
              </p>
              <p className="mt-1 text-sm text-gray-500">
                {currentMeta.description}
              </p>
            </div>

            <div className="mt-2">
              {inlineError && (
                <Alert
                  type="error"
                  message={
                    <div>
                      <div>{inlineError}</div>
                      {inlineError.includes('unavailable') && (
                        <div className="mt-2 text-xs">
                          Need help? Contact{' '}
                          <a
                            href="mailto:support@nyandaruapay.gov.ke"
                            className="underline text-blue-600 hover:text-blue-800"
                          >
                            support@nyandaruapay.gov.ke
                          </a>
                        </div>
                      )}
                    </div>
                  }
                  showIcon
                  className="mb-3 text-xs"
                />
              )}

              <Form
                form={form}
                layout="vertical"
                onFinish={handleNext}
                autoComplete="off"
                className="space-y-0"
              >
                {activeStep === 0 && <Introduction inlineError={inlineError} />}
                {activeStep === 1 && (
                  <Identification form={form} error={inlineError} />
                )}
                {activeStep === 2 && (
                  <OTP
                    form={form}
                    errorProp={inlineError}
                    onResend={handleResendOtp}
                    durationSec={90}
                  />
                )}

                <div className="mt-6 flex flex-col gap-3">
                  <ThemedButton
                    htmlType="submit"
                    theme="primary"
                    variant="solid"
                    text={
                      isSubmitting
                        ? 'Please wait...'
                        : activeStep < 2
                          ? 'Continue'
                          : 'Sign In'
                    }
                    loading={isSubmitting}
                    disabled={isSubmitting || redirecting}
                    className="h-11! w-full! rounded-md! text-[15px]! font-semibold!"
                  />

                  <div className="flex items-center justify-between gap-3">
                    <ThemedButton
                      theme="accent"
                      variant="outline"
                      text={activeStep === 0 ? 'Cancel' : 'Back'}
                      iconPosition="prefix"
                      icon={<FiArrowLeft />}
                      onClick={activeStep === 0 ? handleCancel : handlePrev}
                      disabled={isSubmitting || redirecting}
                      className="h-10! rounded-md! text-xs! font-semibold!"
                    />
                  </div>

                  <div className="mt-0 flex flex-row justify-between items-center w-full text-sm">
                    <Link
                      href="/account/signup"
                      className="group flex items-center"
                    >
                      <span className="group-hover:underline transition-all text-gray-800!">
                        Don&apos;t have an account?{' '}
                      </span>
                      <span className="font-semibold ml-1 text-[var(--color-accent)]!">
                        Sign Up
                      </span>
                      <span className="ml-1 transition-transform translate-x-0 opacity-0 group-hover:translate-x-1 group-hover:opacity-100 text-[var(--color-accent)]!">
                        &rarr;
                      </span>
                    </Link>

                    <span className="text-gray-400 m-0">or</span>

                    <Link
                      href="/account/reset-password/"
                      className="text-[var(--color-accent)]! group flex items-center"
                    >
                      <span className="font-semibold group-hover:underline transition-all">
                        Forgot Password?
                      </span>
                      <span className="ml-1 transition-transform translate-x-0 opacity-0 group-hover:translate-x-1 group-hover:opacity-100">
                        &rarr;
                      </span>
                    </Link>
                  </div>
                </div>
              </Form>
            </div>
          </div>

          <div className="relative z-10 mt-6 flex w-full justify-start text-xs text-gray-400 md:mt-10">
            <AuthFooter />
          </div>
        </div>

        <div className="hidden flex-shrink-0 items-stretch justify-center md:flex">
          <div className="relative h-[100vh] w-[410px] max-w-full">
            <Image
              src="/images/auth-hero.webp"
              alt="Sign in hero"
              fill
              className="object-cover object-top"
            />
          </div>
        </div>
      </div>

      {redirecting && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 backdrop-blur-sm">
          <div className="rounded-2xl bg-white p-6 shadow-xl">
            <div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-transparent" />
            <p className="text-center text-sm font-medium">
              Taking you to your page…
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default SigninPage;
